/**
 * PosterPreviewPanel - 海报预览面板组件
 *
 * 两种展示状态：
 * 1. loading 态：动态渐变背景 + loading 动效（无图片时默认一直 loading）
 * 2. 图片展示：有海报图片 URL 时渲染海报，支持加载失败重试
 */
import React, { useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import BasicImage from '@home/components/BasicImage';
import { IPosterPreviewPanelProps } from '../../types';
import './PosterPreviewPanel.scss';
import magicPosterCustomLoading from '@/pages/aigc/assets/svg/magic-poster-custom-loading.svg';
import posterLoadErrorIcon from '@/pages/aigc/assets/svg/poster-load-error.svg';

export const PosterPreviewPanel: React.FC<IPosterPreviewPanelProps> = ({
  imageUrl,
  isLoading,
  onRetry,
  className,
}) => {
  const { formatMessage } = useIntl();
  
  /** 图片加载状态：loading | success | error */
  const [imageStatus, setImageStatus] = useState<'loading' | 'success' | 'error'>('loading');

  /** Loading 信息组件 - 复用于多处 */
  const LoadingInfo = (
    <div className="dba-poster-preview__loading-info">
      <span className="dba-poster-preview__loading-icon">
        <img src={magicPosterCustomLoading} alt="" />
      </span>
      <span className="dba-poster-preview__loading-text">
        {formatMessage({ id: 'dba_poster_preview_loading' })}
      </span>
    </div>
  );

  /** imageUrl 变化时重置状态 */
  useEffect(() => {
    if (imageUrl) {
      setImageStatus('loading');
    }
  }, [imageUrl]);

  /** 图片加载成功 */
  const handleImageLoad = useCallback(() => {
    setImageStatus('success');
  }, []);

  /** 图片加载失败 */
  const handleImageError = useCallback(() => {
    setImageStatus('error');
  }, []);

  /** 重试按钮点击 */
  const handleRetryClick = useCallback(() => {
    setImageStatus('loading');
    onRetry?.();
  }, [onRetry]);

  /** 是否显示 loading：外部 isLoading 为 true，或没有图片时默认 loading */
  const showLoading = isLoading || !imageUrl;

  /** 是否显示图片加载中 */
  const showImageLoading = !showLoading && imageStatus === 'loading';

  /** 是否显示加载失败 */
  const showError = !showLoading && imageStatus === 'error';

  /** 是否显示图片 */
  const showImage = !showLoading && imageStatus === 'success';

  return (
    <div className={classNames('dba-poster-preview', className)}>
      {showLoading ? (
        // loading 态：动态渐变背景 + 固定 loading 图标
        <div className="dba-poster-preview__loading">
          {LoadingInfo}
        </div>
      ) : (
        // 海报图片展示区域
        <div className="dba-poster-preview__image-container">
          {/* 图片加载中 */}
          {showImageLoading && (
            <div className="dba-poster-preview__image-loading">
              {LoadingInfo}
            </div>
          )}

          {/* 加载失败 */}
          {showError && (
            <div className="dba-poster-preview__error">
              <BasicImage
                src={posterLoadErrorIcon}
                errorSrc={posterLoadErrorIcon}
                className="dba-poster-preview__error-icon"
              />
              <div className="dba-poster-preview__error-text">
                {formatMessage({ id: 'dba_poster_preview_load_error' })}
                <span
                  className="dba-poster-preview__error-retry"
                  onClick={handleRetryClick}
                >
                  {formatMessage({ id: 'dba_poster_preview_retry' })}
                </span>
              </div>
            </div>
          )}

          {/* 原生 img 标签，直接监听 onLoad/onError */}
          <img
            src={imageUrl}
            alt="Magic Poster"
            className={classNames('dba-poster-preview__image', {
              'dba-poster-preview__image--hidden': !showImage,
            })}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        </div>
      )}
    </div>
  );
};

export default PosterPreviewPanel;
